import React from 'react';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import {Link} from 'react-router-dom';
import setDisplayName from 'recompose/setDisplayName';
import withProps from 'recompose/withProps';
import compose from 'recompose/compose';
import chunk from 'lodash/chunk';
import range from 'lodash/range';
import partial from 'lodash/partial';
import includes from 'lodash/includes';
import {Dropdown, Grid, Header, Label} from 'semantic-ui-react';
import {FormattedMessage} from 'react-intl';
import moment from 'moment';
import classnames from 'classnames';
import {zeroPad, bindSelector, SitesByLanguage} from 'utils/DBUtils';
import {
    endCreateStoreEscrowAction,
    setCurrentMonthAction,
    setCurrentYearAction,
    startCreateStoreEscrowAction,
    submitCreateStoreEscrowAction
} from '../actions';
import CreateStoreEscrowModal from './CreateStoreEscrowModal';
import {validYears} from '../utils';
import {getStoreEscrow} from '../selectors';
import LoadingSpinner from "../../../components/common/LoadingSpinner";

const mapStateToProps = state => {
  const {
    selectedLang,
    selectedMonth,
    selectedYear,
    creatingStoreEscrow,
    createStoreEscrowError,
    createdStoreEscrow,
    allowedFutureMonths,
    storeEscrowsById,
    fetchingEscrows
  } = state.rewards;

  return {
    // we want to partially apply the current state of the Redux store to this selector so we don't
    // have to pass down the entire state on props, which could result in unwanted re-renders. in
    // order to do that, first we have to uncurry our selector and then flip its parameter list, and
    // then we can partially apply that state to it.
    getStoreEscrow: bindSelector(getStoreEscrow, state),

    selectedLang,
    site: SitesByLanguage[selectedLang],
    selectedYear,
    selectedMonth,
    createdStoreEscrow,
    createStoreEscrowError,
    creatingStoreEscrow,
    allowedFutureMonths,
    storeEscrows: storeEscrowsById,
    loading: fetchingEscrows
  };
};

const mapDispatchToProps = dispatch => bindActionCreators({
  selectYear: setCurrentYearAction,
  selectMonth: setCurrentMonthAction,
  openCreateStoreEscrowModal: startCreateStoreEscrowAction,
  createNewStoreEscrow: submitCreateStoreEscrowAction,
  closeCreateStoreEscrowModal: endCreateStoreEscrowAction
}, dispatch);

const StorePickerPanel = compose(
  setDisplayName('StorePickerPanel'),
  connect(mapStateToProps, mapDispatchToProps),
  withProps(props => ({
    years: validYears.map(year => ({
      active: false,
      selected: false,
      key: year,
      value: year,
      text: <FormattedMessage id='rewardsStore.dashboard.storePicker.dropdown.option' values={{year}} />
    })),
    selectYear: (event, {value}) => props.selectYear(value),
    createNewStoreEscrow: (budget, copy) => {
      // the API expects the full name of the month, like e.g. "August".
      const monthName = moment().month(props.selectedMonth).format('MMMM');
      props.createNewStoreEscrow(props.selectedLang, props.selectedYear, monthName, budget, copy);
    }
  }))
)(props => (
  <div className='rewards-store-picker'>
    <Header as='h2'>
      <FormattedMessage id='rewardsStore.dashboard.storePicker.header' values={{site: props.site}} />
    </Header>
    <Dropdown
      text={`Store ${props.selectedYear}`}
      options={props.years}
      value={props.selectedYear}
      onChange={props.selectYear}
      selection
      closeOnBlur
      closeOnChange
      openOnFocus={false}
      selectOnBlur={false}
      selectOnNavigation={false} />
    {props.loading ? (
      <LoadingSpinner/>
    ) : (
    <Grid>
      <Grid.Row columns={4}>
        {chunk(range(12), 3).map((items, index) => (
          <Grid.Column key={index} textAlign='center'>
            {items.map(i => {
              const now = moment();
              const month = moment().month(i).year(props.selectedYear);
              const monthNumber = month.month();

              const route = [
                '/rewards',
                props.selectedLang,
                props.selectedYear,
                zeroPad(monthNumber + 1, 2) // show one-indexed month number in the URL
              ].join('/');

              const isInThePast = month.isBefore(now, 'month');
              const isLive = month.isSame(now, 'month');
              const isInTheFuture = month.isAfter(now, 'month');
              const isInBounds = includes(props.allowedFutureMonths, monthNumber);
              const isCurrentMonth = monthNumber === now.month();

              console.log(`now: ${now}`);
              console.log(`month: ${month}`);
              console.log(`is past: ${isInThePast}`);
              console.log(`is future: ${isInTheFuture}`);

              // API data returns one-indexed month numbers, so increment it
              const escrowExists = Boolean(props.getStoreEscrow({
                lang: props.selectedLang,
                year: props.selectedYear,
                month: monthNumber + 1
              }));

              // these classes should be mutually exclusive
              const labelClasses = classnames('store-escrow', {
                'escrow-empty': !escrowExists && isInThePast,
                'escrow-read-only': escrowExists && isInThePast,
                'escrow-live': escrowExists && isLive,
                'escrow-created': escrowExists && isInTheFuture,
                'escrow-in-bounds': !escrowExists && isInTheFuture && isInBounds,
                'escrow-out-of-bounds': !escrowExists && isInTheFuture && !isInBounds
              });

              const clickEnabled = (!escrowExists && isCurrentMonth) || (escrowExists && (isInThePast || isLive)) || (isInTheFuture && isInBounds);
              const labelText = month.format('MMMM');

              const label = clickEnabled ? (
                escrowExists ? (
                  <Label
                    as={Link}
                    to={route}
                    onClick={partial(props.selectMonth, monthNumber)}
                    className={labelClasses}>
                    {labelText}
                  </Label>
                ) : (
                  <Label
                    onClick={() => {
                      props.selectMonth(monthNumber);
                      props.openCreateStoreEscrowModal(monthNumber);
                    }}
                    className={labelClasses}>
                    {labelText}
                  </Label>
                )
              ) : <Label className={labelClasses}>{labelText}</Label>;

              return (
                <Grid.Row
                  columns={1}
                  key={`${props.selectedYear}-${month.month()}`}
                  className='store-picker-item'>
                  {label}
                </Grid.Row>
              );
            })}
          </Grid.Column>
        ))}
      </Grid.Row>
    </Grid>
    )}
    <CreateStoreEscrowModal
      lang={props.selectedLang}
      year={props.selectedYear}
      month={props.selectedMonth}
      isOpen={props.creatingStoreEscrow}
      onSubmit={props.createNewStoreEscrow}
      apiError={props.createStoreEscrowError}
      onClose={props.closeCreateStoreEscrowModal}
      createdStoreEscrow={props.createdStoreEscrow} />
  </div>
));

export default StorePickerPanel;



// WEBPACK FOOTER //
// ./public/pages/Rewards/RewardsDashboard/StorePickerPanel.jsx